import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import config from "../config";
import PageLayout from "../components/ui/PageLayout";
import { buttonClass } from "../components/ui/Button";
import { Alert } from "../components/ui/Alert";
import Reveal from "../components/ui/Reveal";
import { FadeIn } from "../components/ui/FadeIn";
import { EmptyState } from "../components/ui/EmptyState";
import { Panel } from "../components/ui/Panel";
import { StepStrip } from "../components/ui/StepStrip";

const RMAStatus = () => {
    const location = useLocation();
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);

    // Parse query params on mount
    const getInitialValues = () => {
        const params = new URLSearchParams(location.search);
        return {
            rma: params.get("rma") || "",
            email: params.get("email") || ""
        };
    };

    const [rmaNumber, setRmaNumber] = useState(getInitialValues().rma);
    const [email, setEmail] = useState(getInitialValues().email);
    const [rmaData, setRmaData] = useState(null);
    const [messages, setMessages] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [shouldScrollMessages, setShouldScrollMessages] = useState(false);

    // Auto-scroll messages only after sending (not on initial load)
    useEffect(() => {
        if (shouldScrollMessages && messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            setShouldScrollMessages(false);
        }
    }, [messages, shouldScrollMessages]);

    // Message form state
    const [newMessage, setNewMessage] = useState("");
    const [sendingMessage, setSendingMessage] = useState(false);

    // Per-item log upload state: { [itemId]: { file, uploading, success, error } }
    const [itemLogUploads, setItemLogUploads] = useState({});

    const statusSteps = [
        { key: "pending_review", label: "Pending Review", description: "Your RMA is awaiting review" },
        { key: "approved", label: "Approved", description: "RMA approved, preparing shipping label" },
        { key: "awaiting_receipt", label: "Label Ready", description: "Shipping label is ready" },
        { key: "received", label: "Received", description: "We received your items" },
        { key: "in_repair", label: "In Repair", description: "Repair in progress" },
        { key: "quality_check", label: "Quality Check", description: "Final quality inspection" },
        { key: "shipped", label: "Shipped Back", description: "Your items are on their way back" },
        { key: "completed", label: "Completed", description: "RMA process complete" }
    ];

    const getStatusIndex = (status) => {
        if (status === "rejected" || status === "on_hold" || status === "cancelled" || status === "partially_approved") {
            return -1;
        }
        return statusSteps.findIndex(s => s.key === status);
    };

    // Auto-lookup on mount if we have query params
    useEffect(() => {
        const initial = getInitialValues();
        if (initial.rma && initial.email) {
            lookupRMA(initial.rma, initial.email);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const lookupRMA = async (rma = rmaNumber, emailAddr = email) => {
        if (!rma.trim() || !emailAddr.trim()) {
            setError("Please enter both RMA number and email address");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(
                `${config.API_ENDPOINT}/rma-public/lookup?rma_number=${encodeURIComponent(rma.trim())}&email=${encodeURIComponent(emailAddr.trim())}`
            );
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "RMA not found");
            }

            // Combine rma header with items and shipments
            setRmaData({
                ...data.rma,
                items: data.items || [],
                shipments: data.shipments || []
            });
            setMessages(data.messages || []);
            setHistory(data.history || []);
        } catch (err) {
            setError(err.message);
            setRmaData(null);
        } finally {
            setLoading(false);
        }
    };

    const handleLookup = (e) => {
        e.preventDefault();
        lookupRMA();
    };

    const sendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !rmaData) return;

        setSendingMessage(true);

        try {
            const response = await fetch(`${config.API_ENDPOINT}/rma-public/customer-message`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    rma_number: rmaData.rma_number,
                    email: email,
                    message: newMessage.trim()
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Failed to send message");
            }

            // Add message to list and trigger scroll
            setMessages(prev => [...prev, data.message]);
            setNewMessage("");
            setShouldScrollMessages(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setSendingMessage(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        return new Date(dateStr).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const formatDateShort = (dateStr) => {
        if (!dateStr) return "";
        return new Date(dateStr).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric"
        });
    };

    // Handle log file selection for a specific item
    const handleItemLogFileChange = (itemId, e) => {
        const file = e.target.files[0];
        if (file && file.name.toLowerCase().endsWith('.ulg')) {
            setItemLogUploads(prev => ({
                ...prev,
                [itemId]: { file, uploading: false, success: false, error: null }
            }));
        }
    };

    // Upload log file for a specific item
    const uploadItemLogFile = async (itemId, serialNumber) => {
        const uploadState = itemLogUploads[itemId];
        if (!uploadState?.file || !rmaData) return;

        setItemLogUploads(prev => ({
            ...prev,
            [itemId]: { ...prev[itemId], uploading: true, error: null }
        }));

        try {
            const formData = new FormData();
            formData.append('file', uploadState.file);
            formData.append('rma_number', rmaData.rma_number);
            formData.append('email', email);
            formData.append('serial_number', serialNumber);

            const response = await fetch(`${config.API_ENDPOINT}/rma-public/upload-log`, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Upload failed');
            }

            setItemLogUploads(prev => ({
                ...prev,
                [itemId]: { file: null, uploading: false, success: true, error: null }
            }));

            // Clear file input
            const fileInput = document.getElementById(`log_file_${itemId}`);
            if (fileInput) fileInput.value = '';
        } catch (err) {
            setItemLogUploads(prev => ({
                ...prev,
                [itemId]: { ...prev[itemId], uploading: false, error: err.message }
            }));
        }
    };

    const getItemStatusBadge = (item) => {
        const status = item.item_status;
        const colors = {
            pending_review: "bg-yellow-800 text-yellow-200",
            approved: "bg-green-800 text-green-200",
            rejected: "bg-red-800 text-red-200"
        };
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] || "bg-gray-700 text-on-surface"}`}>
                {status.replace(/_/g, " ")}
            </span>
        );
    };

    const getRepairStatusBadge = (item) => {
        if (item.item_status !== "approved") return null;
        const status = item.repair_status;
        if (!status || status === "not_started") return null;

        const colors = {
            in_progress: "bg-blue-800 text-blue-200",
            completed: "bg-green-800 text-green-200",
            cannot_repair: "bg-red-800 text-red-200"
        };
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] || "bg-gray-700 text-on-surface"}`}>
                Repair: {status.replace(/_/g, " ")}
            </span>
        );
    };

    const inputClasses = `w-full px-3 py-2 bg-surface-container-high shadow-recess border border-transparent rounded
        text-white placeholder:text-on-surface-variant
        focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30
        focus:shadow-[0_0_12px_rgba(255,204,51,0.15)]
        transition-all duration-200`;

    // Use derived_status from the new schema
    const currentStatus = rmaData?.derived_status || rmaData?.status;
    const currentStatusIndex = currentStatus ? getStatusIndex(currentStatus) : -1;

    // Group shipments by direction
    const inboundShipments = rmaData?.shipments?.filter(s => s.direction === "inbound") || [];
    const outboundShipments = rmaData?.shipments?.filter(s => s.direction === "outbound") || [];

    return (
        <div style={{ fontFamily: "RobotoMono, sans-serif" }}>
        <PageLayout width="narrow" kind="workspace" className="pt-10 min-h-screen">
            <div>
                {/* Lookup Form */}
                <div
                    className="p-6 bg-[#2a2a2a] text-white rounded-lg border-2 border-primary/30 mb-6 max-w-2xl"
                    style={{
                        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.5), 0 0 40px rgba(255, 204, 51, 0.08)"
                    }}
                >
                    <h3 className="text-xl font-bold mb-4 text-primary">Check RMA Status</h3>

                    <form onSubmit={handleLookup} className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1">
                            <input
                                type="text"
                                value={rmaNumber}
                                onChange={(e) => setRmaNumber(e.target.value)}
                                className={inputClasses}
                                placeholder="RMA Number (e.g., RMA-2025-00001)"
                                autoComplete="on"
                                name="rma_number"
                                required
                            />
                        </div>
                        <div className="flex-1">
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className={inputClasses}
                                placeholder="Email address"
                                autoComplete="email"
                                name="email"
                                required
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className={buttonClass('tonal')}
                        >
                            {loading ? "Looking up..." : "Lookup"}
                        </button>
                    </form>

                    <Reveal show={!!error}>
                        <Alert className="mt-4">{error}</Alert>
                    </Reveal>
                </div>

                {/* RMA Details */}
                {rmaData && (
                    <FadeIn key={rmaData.rma_number}>
                        {/* Status Overview */}
                        <Panel label="Status overview" padding="md" className="mb-6">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h4 className="text-lg font-bold">{rmaData.rma_number}</h4>
                                    <p className="text-on-surface-variant text-sm">
                                        Submitted {formatDate(rmaData.created_at)}
                                    </p>
                                    {rmaData.estimated_completion_date && (
                                        <p className="text-on-surface-variant text-sm">
                                            Est. Completion: {formatDateShort(rmaData.estimated_completion_date)}
                                        </p>
                                    )}
                                </div>
                                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                                    currentStatus === "completed" ? "bg-green-800 text-green-200" :
                                    currentStatus === "rejected" ? "bg-red-800 text-red-200" :
                                    currentStatus === "on_hold" ? "bg-yellow-800 text-yellow-200" :
                                    currentStatus === "partially_approved" ? "bg-orange-800 text-orange-200" :
                                    "bg-blue-800 text-blue-200"
                                }`}>
                                    {currentStatus?.replace(/_/g, " ").toUpperCase()}
                                </div>
                            </div>

                            {/* Status Progress Bar */}
                            {currentStatusIndex >= 0 && (
                                <div className="mt-6">
                                    <StepStrip
                                        label="RMA progress"
                                        steps={statusSteps.map((step, idx) => ({
                                            key: step.key,
                                            label: step.label,
                                            detail: idx === currentStatusIndex ? step.description : null,
                                            state: idx < currentStatusIndex || (idx === statusSteps.length - 1 && idx === currentStatusIndex) ? "done" : idx === currentStatusIndex ? "current" : "future"
                                        }))}
                                    />
                                </div>
                            )}

                            {/* Special status messages */}
                            <Reveal show={currentStatus === "rejected"}>
                                <Alert className="mt-4">
                                    Your RMA request was not approved. Please check messages below for details.
                                </Alert>
                            </Reveal>

                            <Reveal show={currentStatus === "partially_approved"}>
                                <Alert tone="warning" className="mt-4">
                                    Some items in your RMA were approved while others were rejected. See item details below.
                                </Alert>
                            </Reveal>

                            <Reveal show={currentStatus === "on_hold"}>
                                <Alert tone="warning" className="mt-4">
                                    Your RMA is on hold. Please check messages below for required action.
                                </Alert>
                            </Reveal>
                        </Panel>

                        {/* Items List */}
                        {rmaData.items && rmaData.items.length > 0 && (
                            <Panel title={`Items (${rmaData.items.length})`} className="mb-6">

                                <div className="space-y-3">
                                    {rmaData.items.map((item, idx) => (
                                        <div
                                            key={item.id || idx}
                                            className={`p-4 rounded border ${
                                                item.item_status === "rejected"
                                                    ? "bg-red-900/20 border-red-800"
                                                    : item.item_status === "approved"
                                                    ? "bg-green-900/10 border-green-800/50"
                                                    : "bg-surface-container border-transparent"
                                            }`}
                                        >
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="font-medium">
                                                            {item.catalog_name || item.custom_description || "Item"}
                                                        </span>
                                                        {item.quantity > 1 && (
                                                            <span className="text-on-surface-variant">×{item.quantity}</span>
                                                        )}
                                                    </div>
                                                    {item.serial_number && (
                                                        <p className="text-primary font-bold text-lg font-mono">
                                                            {item.serial_number}
                                                        </p>
                                                    )}
                                                    {item.charger_id && (
                                                        <p className="text-sm text-on-surface-variant font-mono">
                                                            ID: <span className="text-primary font-bold">{item.charger_id}</span>
                                                        </p>
                                                    )}
                                                    {item.issue_category && (
                                                        <span className="inline-block mt-1 px-2 py-0.5 bg-blue-900/50 text-blue-300 text-xs rounded">
                                                            {item.issue_category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                        </span>
                                                    )}
                                                    {item.issue_description && (
                                                        <p className="text-sm text-on-surface-variant mt-1">
                                                            {item.issue_description}
                                                        </p>
                                                    )}
                                                    {item.rejection_reason && (
                                                        <p className="text-sm text-red-400 mt-1">
                                                            Reason: {item.rejection_reason}
                                                        </p>
                                                    )}
                                                    {item.repair_notes && (
                                                        <p className="text-sm text-blue-400 mt-1">
                                                            Repair notes: {item.repair_notes}
                                                        </p>
                                                    )}

                                                    {/* Per-drone log upload */}
                                                    {item.catalog_item_type === 'drone' && item.serial_number && (
                                                        <div className="mt-3 pt-3 border-t border-gray-700">
                                                            {/* Show existing log status */}
                                                            {(item.has_log || itemLogUploads[item.id]?.success) && !itemLogUploads[item.id]?.showUploader && (
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-green-400 text-xs">
                                                                        ✓ Log uploaded
                                                                    </span>
                                                                    <button
                                                                        onClick={() => setItemLogUploads(prev => ({
                                                                            ...prev,
                                                                            [item.id]: { ...prev[item.id], showUploader: true }
                                                                        }))}
                                                                        className={buttonClass('quiet', 'sm')}
                                                                    >
                                                                        Upload another?
                                                                    </button>
                                                                </div>
                                                            )}

                                                            {/* Show uploader if no log exists or user clicked "Upload another?" */}
                                                            {(!item.has_log && !itemLogUploads[item.id]?.success) || itemLogUploads[item.id]?.showUploader ? (
                                                                <>
                                                                    <p className="text-xs text-on-surface-variant mb-2">
                                                                        Upload flight log (.ulg) for this drone
                                                                    </p>
                                                                    <div className="flex gap-2 items-center">
                                                                        <input
                                                                            type="file"
                                                                            id={`log_file_${item.id}`}
                                                                            accept=".ulg"
                                                                            onChange={(e) => handleItemLogFileChange(item.id, e)}
                                                                            className="flex-1 text-sm text-on-surface-variant
                                                                                file:mr-2 file:py-1 file:px-2 file:rounded
                                                                                file:border-0 file:bg-gray-700 file:text-white file:text-xs
                                                                                file:cursor-pointer hover:file:bg-gray-750"
                                                                        />
                                                                        <button
                                                                            onClick={() => uploadItemLogFile(item.id, item.serial_number)}
                                                                            disabled={!itemLogUploads[item.id]?.file || itemLogUploads[item.id]?.uploading}
                                                                            className={buttonClass('quiet', 'sm')}
                                                                        >
                                                                            {itemLogUploads[item.id]?.uploading ? "Uploading..." : "Upload"}
                                                                        </button>
                                                                    </div>
                                                                    <Reveal show={!!itemLogUploads[item.id]?.file}>
                                                                        <p className="mt-1 text-xs text-on-surface-variant">
                                                                            Selected: {itemLogUploads[item.id]?.file?.name}
                                                                        </p>
                                                                    </Reveal>
                                                                    <Reveal show={!!itemLogUploads[item.id]?.error}>
                                                                        <p className="mt-1 text-xs text-red-400">
                                                                            {itemLogUploads[item.id]?.error}
                                                                        </p>
                                                                    </Reveal>
                                                                </>
                                                            ) : null}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex flex-col items-end gap-1">
                                                    {getItemStatusBadge(item)}
                                                    {getRepairStatusBadge(item)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Panel>
                        )}

                        {/* Tracking Info - Now supports multiple shipments */}
                        {(inboundShipments.length > 0 || outboundShipments.length > 0) && (
                            <Panel title="Shipping Information" className="mb-6">

                                {/* Inbound Shipments */}
                                {inboundShipments.length > 0 && (
                                    <div className="mb-4">
                                        <p className="text-on-surface-variant text-sm mb-2">
                                            Inbound ({inboundShipments.length} {inboundShipments.length === 1 ? "box" : "boxes"} to send)
                                        </p>
                                        <div className="space-y-3">
                                            {inboundShipments.map((shipment, idx) => (
                                                <Panel key={shipment.id || idx} on="panel" padding="sm" label={`Box ${idx + 1}`}>
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <span className="text-on-surface-variant text-sm">Box {idx + 1}</span>
                                                            {shipment.box_name && (
                                                                <span className="text-on-surface-variant text-sm ml-2">({shipment.box_name})</span>
                                                            )}
                                                        </div>
                                                        {shipment.tracking_number && (
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-mono text-sm">{shipment.tracking_number}</span>
                                                                <a
                                                                    href={`https://www.fedex.com/fedextrack/?tracknumbers=${shipment.tracking_number}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-primary hover:underline text-sm"
                                                                >
                                                                    Track
                                                                </a>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {shipment.label_url && (
                                                        <div className="flex gap-2 mt-2">
                                                            <a
                                                                href={shipment.label_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className={buttonClass('quiet')}
                                                            >
                                                                Download Label
                                                            </a>
                                                            <button
                                                                onClick={() => {
                                                                    const printWindow = window.open(shipment.label_url, '_blank');
                                                                    if (printWindow) {
                                                                        printWindow.onload = () => {
                                                                            printWindow.print();
                                                                        };
                                                                    }
                                                                }}
                                                                className={buttonClass('filled')}
                                                            >
                                                                Print Label
                                                            </button>
                                                        </div>
                                                    )}
                                                </Panel>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Outbound Shipments */}
                                {outboundShipments.length > 0 && (
                                    <div>
                                        <p className="text-on-surface-variant text-sm mb-2">
                                            Return ({outboundShipments.length} {outboundShipments.length === 1 ? "box" : "boxes"} on the way)
                                        </p>
                                        <div className="space-y-3">
                                            {outboundShipments.map((shipment, idx) => (
                                                <Panel key={shipment.id || idx} on="panel" padding="sm" label={`Box ${idx + 1}`}>
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <span className="text-on-surface-variant text-sm">Box {idx + 1}</span>
                                                            {shipment.box_name && (
                                                                <span className="text-on-surface-variant text-sm ml-2">({shipment.box_name})</span>
                                                            )}
                                                        </div>
                                                        {shipment.tracking_number && (
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-mono text-sm">{shipment.tracking_number}</span>
                                                                <a
                                                                    href={`https://www.fedex.com/fedextrack/?tracknumbers=${shipment.tracking_number}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-primary hover:underline text-sm"
                                                                >
                                                                    Track
                                                                </a>
                                                            </div>
                                                        )}
                                                    </div>
                                                </Panel>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </Panel>
                        )}

                        {/* Messages */}
                        <Panel title="Messages" className="mb-6">

                            <div ref={messagesContainerRef} className="space-y-4 max-h-96 overflow-y-auto mb-4">
                                {messages.filter(m => !m.is_internal).length === 0 ? (
                                    <EmptyState variant="area">No messages yet</EmptyState>
                                ) : (
                                    messages
                                        .filter(m => !m.is_internal)
                                        .map((msg, idx) => (
                                            <div
                                                key={idx}
                                                className={`p-3 rounded ${
                                                    msg.sender_type === "customer"
                                                        ? "bg-blue-900/30 border-l-4 border-blue-500 ml-8"
                                                        : "bg-surface-container border-l-4 border-outline-variant mr-8"
                                                }`}
                                            >
                                                <div className="flex justify-between text-sm text-on-surface-variant mb-1">
                                                    <span>{msg.sender_name || msg.sender_type}</span>
                                                    <span>{formatDate(msg.created_at)}</span>
                                                </div>
                                                <p className="whitespace-pre-wrap">{msg.message_text}</p>
                                            </div>
                                        ))
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Send Message Form */}
                            <form onSubmit={sendMessage} className="flex gap-2">
                                <input
                                    type="text"
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    className={`${inputClasses} flex-1`}
                                    placeholder="Type a message..."
                                />
                                <button
                                    type="submit"
                                    disabled={sendingMessage || !newMessage.trim()}
                                    className={buttonClass('filled')}
                                >
                                    {sendingMessage ? "..." : "Send"}
                                </button>
                            </form>
                        </Panel>

                        {/* Status History */}
                        <Panel title="Status History">

                            <div className="space-y-3">
                                {history.length === 0 ? (
                                    <EmptyState variant="area">No history yet</EmptyState>
                                ) : (
                                    history.map((item, idx) => (
                                        <div key={idx} className="flex items-start gap-3">
                                            <div className={`w-2 h-2 mt-2 rounded-full ${
                                                item.change_type === "item_approval"
                                                    ? item.new_value === "approved" ? "bg-green-500" : "bg-red-500"
                                                    : item.change_type === "item_repair"
                                                    ? "bg-blue-500"
                                                    : "bg-outline"
                                            }`} />
                                            <div className="flex-1">
                                                <div className="flex justify-between">
                                                    <span className="text-on-surface">
                                                        {item.change_type === "item_approval" ? (
                                                            <>
                                                                Item {item.new_value?.replace(/_/g, " ")}
                                                                {item.item_serial && (
                                                                    <span className="text-on-surface-variant ml-1">
                                                                        ({item.item_serial})
                                                                    </span>
                                                                )}
                                                            </>
                                                        ) : item.change_type === "item_repair" ? (
                                                            <>
                                                                Repair {item.new_value?.replace(/_/g, " ")}
                                                                {item.item_serial && (
                                                                    <span className="text-on-surface-variant ml-1">
                                                                        ({item.item_serial})
                                                                    </span>
                                                                )}
                                                            </>
                                                        ) : (
                                                            (item.new_value || item.new_status || "").replace(/_/g, " ")
                                                        )}
                                                    </span>
                                                    <span className="text-on-surface-variant text-sm">
                                                        {formatDate(item.created_at)}
                                                    </span>
                                                </div>
                                                {item.notes && (
                                                    <p className="text-on-surface-variant text-sm">{item.notes}</p>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </Panel>
                    </FadeIn>
                )}

                {/* Back to Submit Link */}
                <Reveal show={!rmaData}>
                    <p className="text-center text-on-surface-variant mt-6">
                        Need to submit a new RMA?{" "}
                        <Link to="/rma/submit" className="text-primary hover:underline">
                            Submit Request
                        </Link>
                    </p>
                </Reveal>
            </div>
        </PageLayout>
        </div>
    );
};

export default RMAStatus;
